/* eslint-disable react/destructuring-assignment */
import React, { FC, useState } from 'react';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { FaCheck } from 'react-icons/fa';
import { SelectComponentsProps } from 'react-select/src/Select';
import { useTranslation } from 'react-i18next';
import { ISelectProps } from './types';

/*
 * Abstracted select component through `react-select`.
 * Used when single select.
 */
const SingleSelect: FC<ISelectProps> = ({
  placeholder,
  isClearable,
  isCreatable,
  isDisabled,
  isSearchable,
  options,
  noOptionsMessage,
  createMessage,
  value,
  onChange,
  className = '',
  hasOptionTooltip,
  limitOptions,
  props,
  ariaLabel,
  disableFilterOption,
  testId,
}) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState('');

  // Customize option
  const Option = (optionProps: any) => {
    const { label, data, isSelected } = optionProps;
    const { label_en: labelEn, status } = data;

    return (
      <div title={hasOptionTooltip ? t(`table.${status}Status`) : ''}>
        <components.Option {...optionProps}>
          <label
            htmlFor={label}
            className="mb-1 mt-1"
          >
            {label}
          </label>
          {isSelected && (
            <FaCheck
              size={12}
              style={{ color: '#32bfa3' }}
            />
          )}
        </components.Option>
      </div>
    );
  };

  const filterOption = (option: any) => {
    const searchTermLower = searchTerm.toLowerCase();

    if (!searchTerm) {
      return true;
    }
    const labelMatch = typeof option.label === 'string' && option.label.toLowerCase().includes(searchTermLower);

    // Filter children if the option has child options
    if (option.options) {
      const filteredChildren = option.options.filter((child: any) =>
        child.label.toLowerCase().includes(searchTermLower)
      );
      if (filteredChildren.length > 0) {
        return { ...option, options: filteredChildren }; // Include only matching children
      }
    }

    return labelMatch;
  };

  // Render options and add `allOption` if needed
  const renderOptions = () => {
    let selectOptions = options?.slice();

    const limit = process.env.REACT_APP_OPTION_LIMIT ? parseInt(process.env.REACT_APP_OPTION_LIMIT, 10) : 100;

    // Filter options with or without children based on the search term
    const filterOptionsWithChildren = (option: any) => {
      const searchTermLower = searchTerm.toLowerCase(); // Convert search term to lower case once, to optimize

      const matchSearchTerm = (label: any) => {
        // Convert any type of label to a string and to lowercase
        const labelStr = String(label).toLowerCase();
        return labelStr.includes(searchTermLower);
      };

      if (option.options) {
        // Apply filtering logic only if the option has children
        const filteredChildren = option.options.filter((child: any) => matchSearchTerm(child.label));
        if (filteredChildren.length > 0) {
          return { ...option, options: filteredChildren }; // Include only matching children
        }
        return false; // If no children match, exclude this parent
      }

      // Use the matchSearchTerm function for consistency
      return matchSearchTerm(option.label) ? option : false; // For options without children, retain existing logic
    };

    if (searchTerm) {
      // Apply filtering only when there is a search term
      if (selectOptions) {
        selectOptions = selectOptions.map(filterOptionsWithChildren).filter(option => option !== false);
      }
    }

    if (limitOptions && selectOptions) {
      selectOptions = selectOptions.slice(0, limit);
    }

    return selectOptions;
  };

  // Handle input change
  const handleInputChange = (inputValue: string) => {
    setSearchTerm(inputValue);
    return inputValue;
  };

  const componentProps: SelectComponentsProps = {
    ...props,
    value: value ?? props?.value ?? null,
    onChange: onChange ?? props?.onChange,
    className: `c-custom-select ${className}`,
    classNamePrefix: 'cs',
    placeholder: placeholder || t('form.select'),
    isDisabled,
    isClearable,
    isSearchable,
    components: { Option },
    hideSelectedOptions: false,
    options: renderOptions(),
    filterOption: null,
    onInputChange: handleInputChange,
    noOptionsMessage: () => noOptionsMessage || t('form.noOptions'),
    theme: (theme: any) => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: '#32bfa3',
        primary25: '#f2f3f8',
        primary50: '#e8e9ef',
        neutral50: '#ced4da',
      },
    }),
  };

  if (isCreatable)
    return (
      <div data-testid={testId}>
        <CreatableSelect
          {...componentProps}
          aria-label={ariaLabel}
          formatCreateLabel={(label: string) => (
            <span>
              {`${createMessage || t('form.create')} `}
              <span className="font-weight-bold">{label}</span>
            </span>
          )}
        />
      </div>
    );

  return (
    <div data-testid={testId}>
      <Select
        {...componentProps}
        aria-label={ariaLabel}
      />
    </div>
  );
};

export default SingleSelect;
